import { gsap } from "@gsap";

const token = (name) => getComputedStyle(document.documentElement).getPropertyValue(`--pk-not-found-${name}`).trim().replace(/^["']|["']$/g, "");
const number = (name, fallback) => {
	const value = parseFloat(token(name));
	return Number.isNaN(value) ? fallback : value;
};
const list = (name, fallback) => (token(name) || fallback).split(",").map((value) => parseFloat(value) || 0);

function settings() {
	return {
		parallax: number("parallax", 24),
		digitDepths: list("digit-depths", "0.6, 1.2, 0.8"),
		shapeDepths: list("shape-depths", "1.6, 2.2, 1.4, 2.6"),
		pupilRange: number("pupil-range", 0.8),
		follow: number("follow-duration", 0.6),
		eyeFollow: number("eye-follow-duration", 0.3),
		intro: number("intro-duration", 1.2),
		introScale: number("intro-scale", 0.4),
		introRotate: number("intro-rotate", 30),
		stagger: number("intro-stagger", 0.08),
		ease: token("intro-ease") || "back.out(1.6)",
		float: number("float-duration", 3.2),
		floatStep: number("float-step", 0.4),
		floatRotate: number("float-rotate", 8),
		floatDistance: number("float-distance", 12),
		look: number("look-duration", 1.4),
		lookPause: number("look-pause", 0.7),
	};
}

function pupilReach(pupil, range) {
	const zero = pupil.parentElement;
	const border = parseFloat(getComputedStyle(zero).borderTopWidth) || 0;
	return Math.max(0, (zero.offsetWidth - border * 2 - pupil.offsetWidth) / 2) * range;
}

function initNotFound() {
	document.querySelectorAll(".not_found-layout").forEach((layout) => {
		const visual = layout.querySelector("[data-not-found-visual]");
		if (!visual) return;

		const digits = gsap.utils.toArray(visual.querySelectorAll("[data-not-found-digit]"));
		const shapes = gsap.utils.toArray(visual.querySelectorAll("[data-not-found-shape]"));
		const pupils = gsap.utils.toArray(visual.querySelectorAll("[data-not-found-pupil]"));

		gsap.matchMedia().add(
			{
				pointer: "(hover: hover) and (prefers-reduced-motion: no-preference)",
				touch: "(hover: none) and (prefers-reduced-motion: no-preference)",
			},
			(context) => {
				const s = settings();

				gsap.from([...digits, ...shapes], {
					autoAlpha: 0,
					scale: s.introScale,
					rotation: () => gsap.utils.random(-s.introRotate, s.introRotate),
					duration: s.intro,
					ease: s.ease,
					stagger: s.stagger,
				});

				shapes.forEach((shape, i) => {
					const direction = i % 2 ? 1 : -1;
					gsap.to(shape, {
						rotation: direction * s.floatRotate,
						yPercent: -direction * s.floatDistance,
						duration: s.float + i * s.floatStep,
						ease: "sine.inOut",
						repeat: -1,
						yoyo: true,
						delay: s.intro,
					});
				});

				if (context.conditions.touch) {
					pupils.forEach((pupil) => {
						const reach = () => pupilReach(pupil, s.pupilRange);
						gsap.to(pupil, {
							x: () => gsap.utils.random(-reach(), reach()),
							y: () => gsap.utils.random(-reach(), reach()),
							duration: s.look,
							ease: "power3.inOut",
							repeat: -1,
							repeatDelay: s.lookPause,
							repeatRefresh: true,
							delay: s.intro,
						});
					});
					return;
				}

				const layers = [
					...digits.map((el, i) => ({ el, depth: s.digitDepths[i % s.digitDepths.length] })),
					...shapes.map((el, i) => ({ el, depth: s.shapeDepths[i % s.shapeDepths.length] })),
				];
				const movers = layers.map(({ el, depth }) => ({
					depth,
					x: gsap.quickTo(el, "x", { duration: s.follow, ease: "power3.out" }),
					y: gsap.quickTo(el, "y", { duration: s.follow, ease: "power3.out" }),
				}));
				const eyes = pupils.map((pupil) => ({
					pupil,
					x: gsap.quickTo(pupil, "x", { duration: s.eyeFollow, ease: "power3.out" }),
					y: gsap.quickTo(pupil, "y", { duration: s.eyeFollow, ease: "power3.out" }),
				}));

				const onMove = (event) => {
					const box = visual.getBoundingClientRect();
					const nx = gsap.utils.clamp(-1, 1, (event.clientX - (box.left + box.width / 2)) / (window.innerWidth / 2));
					const ny = gsap.utils.clamp(-1, 1, (event.clientY - (box.top + box.height / 2)) / (window.innerHeight / 2));

					movers.forEach((mover) => {
						mover.x(nx * mover.depth * s.parallax);
						mover.y(ny * mover.depth * s.parallax);
					});

					eyes.forEach((eye) => {
						const zero = eye.pupil.parentElement.getBoundingClientRect();
						const angle = Math.atan2(event.clientY - (zero.top + zero.height / 2), event.clientX - (zero.left + zero.width / 2));
						const reach = pupilReach(eye.pupil, s.pupilRange);
						eye.x(Math.cos(angle) * reach);
						eye.y(Math.sin(angle) * reach);
					});
				};

				window.addEventListener("pointermove", onMove, { passive: true });
				return () => window.removeEventListener("pointermove", onMove);
			}
		);
	});
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initNotFound);
} else {
	initNotFound();
}
